import { useEffect, useMemo, useState } from 'react';
import { apiClient } from '../../api/client';
import type { Category as ApiCategory } from '../../api/types';

interface CatalogProps {
  onClose: () => void;
  onSelectCategory?: (category: { id: string; name: string }) => void;
}

interface CatalogNode {
  id: string;
  name: string;
  children: CatalogNode[];
}

function mapCategoryToNode(category: ApiCategory): CatalogNode {
  return {
    id: category.id,
    name: category.name,
    children: (category.children ?? []).map(mapCategoryToNode),
  };
}

function buildTreeFromFlat(categories: ApiCategory[]): CatalogNode[] {
  const byId = new Map<string, CatalogNode>();
  const parentById = new Map<string, string | undefined>();

  for (const category of categories) {
    byId.set(category.id, { id: category.id, name: category.name, children: [] });
    parentById.set(category.id, category.parent_id);
  }

  const roots: CatalogNode[] = [];

  for (const category of categories) {
    const node = byId.get(category.id);
    if (!node) continue;

    const parentId = parentById.get(category.id);
    if (parentId && byId.has(parentId)) {
      byId.get(parentId)!.children.push(node);
    } else {
      roots.push(node);
    }
  }

  return roots;
}

export default function Catalog({ onClose, onSelectCategory }: CatalogProps) {
  const [categories, setCategories] = useState<CatalogNode[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const [selectedSubcategoryId, setSelectedSubcategoryId] = useState<string | null>(null);

  useEffect(() => {
    const loadCategories = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await apiClient.getCategories({ active_only: true });
        if (!response.success || !response.data) {
          throw new Error(response.error || 'Не удалось загрузить каталог');
        }

        const source = response.data;
        const mapped = source.some((item) => (item.children ?? []).length > 0)
          ? source.map(mapCategoryToNode)
          : buildTreeFromFlat(source);

        setCategories(mapped);
        // Важно: по умолчанию ничего не выбираем
        setSelectedCategoryId(null);
        setSelectedSubcategoryId(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error occurred');
      } finally {
        setIsLoading(false);
      }
    };

    loadCategories();
  }, []);

  const selectedCategory = useMemo(
    () => categories.find((item) => item.id === selectedCategoryId) ?? null,
    [categories, selectedCategoryId]
  );

  const selectedSubcategory = useMemo(
    () => selectedCategory?.children.find((item) => item.id === selectedSubcategoryId) ?? null,
    [selectedCategory, selectedSubcategoryId]
  );

  const handleLeafSelect = (node: CatalogNode) => {
    onSelectCategory?.({ id: node.id, name: node.name });
    onClose();
  };

  const mobileStep = selectedSubcategory ? 3 : selectedCategory ? 2 : 1;

  return (
    <div
      className="fixed inset-0 z-[70] bg-black/25 backdrop-blur-[1px]"
      onClick={onClose}
    >
      <div
        className="absolute inset-x-3 top-[102px] bottom-3 overflow-hidden rounded-[24px] bg-white shadow-2xl sm:inset-x-6 sm:top-[120px] lg:left-1/2 lg:right-auto lg:top-[120px] lg:bottom-auto lg:min-h-[560px] lg:w-[min(1520px,calc(100vw-48px))] lg:-translate-x-1/2 lg:rounded-[20px] lg:p-[20px]"
        onClick={(e) => e.stopPropagation()}
      >
        {isLoading ? (
          <div className="flex h-full min-h-[420px] items-center justify-center px-6 text-center text-[18px] text-gray-600 sm:text-[22px] lg:h-[520px]">Загрузка каталога...</div>
        ) : error ? (
          <div className="flex h-full min-h-[420px] flex-col items-center justify-center gap-[20px] px-6 text-center lg:h-[520px]">
            <div className="text-[18px] text-red-600 sm:text-[22px]">Ошибка загрузки: {error}</div>
            <button
              onClick={onClose}
              className="h-[50px] px-[25px] bg-[#5d76cb] text-white rounded-[12px] hover:bg-[#4d66bb]"
            >
              Закрыть
            </button>
          </div>
        ) : categories.length === 0 ? (
          <div className="flex h-full min-h-[420px] flex-col items-center justify-center gap-[20px] px-6 text-center lg:h-[520px]">
            <div className="text-[18px] text-gray-600 sm:text-[22px]">Категории не найдены</div>
            <button
              onClick={onClose}
              className="h-[50px] px-[25px] bg-[#5d76cb] text-white rounded-[12px] hover:bg-[#4d66bb]"
            >
              Закрыть
            </button>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between border-b border-[#eef1fb] px-4 py-4 lg:hidden">
              <div>
                <div className="text-[20px] font-bold text-black">Каталог</div>
                <div className="mt-1 text-[13px] text-[#6a7698]">
                  {mobileStep === 1 ? 'Выберите категорию' : mobileStep === 2 ? 'Выберите подкатегорию' : 'Выберите раздел'}
                </div>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="flex h-[38px] w-[38px] items-center justify-center rounded-full bg-[#f3f5fc] text-[22px] text-[#4d66bb]"
                aria-label="Закрыть каталог"
              >
                ×
              </button>
            </div>

            <div className="hidden grid-cols-3 gap-[20px] lg:grid">
              <div className="bg-[#f7f7f7] rounded-[16px] p-[16px] min-h-[520px]">
                <h3 className="text-[20px] font-bold mb-[12px] px-[12px]">Категории</h3>
                <div className="flex flex-col gap-[8px] max-h-[460px] overflow-auto pr-[8px]">
                  {categories.map((category) => {
                    const isSelected = category.id === selectedCategoryId;
                    return (
                      <button
                        key={category.id}
                        onClick={() => {
                          if (category.children.length === 0) {
                            handleLeafSelect(category);
                            return;
                          }
                          setSelectedCategoryId(category.id);
                          setSelectedSubcategoryId(null);
                        }}
                        className={`text-left px-[12px] py-[10px] rounded-[10px] transition-all duration-200 ${
                          isSelected ? 'bg-[#5d76cb] text-white' : 'bg-white text-black hover:bg-[#eaeaea]'
                        }`}
                      >
                        {category.name}
                      </button>
                    );
                  })}
                </div>
              </div>

              {selectedCategory && (
                <div className="bg-[#f7f7f7] rounded-[16px] p-[16px] min-h-[520px]">
                  <h3 className="text-[20px] font-bold mb-[12px] px-[12px]">Подкатегории</h3>
                  {selectedCategory.children.length === 0 ? (
                    <div className="text-gray-500">Нет подкатегорий</div>
                  ) : (
                    <div className="flex flex-col gap-[8px] max-h-[460px] overflow-auto pr-[8px]">
                      {selectedCategory.children.map((subcategory) => {
                        const isSelected = subcategory.id === selectedSubcategoryId;
                        return (
                          <button
                            key={subcategory.id}
                            onClick={() => {
                              if (subcategory.children.length === 0) {
                                handleLeafSelect(subcategory);
                                return;
                              }
                              setSelectedSubcategoryId(subcategory.id);
                            }}
                            className={`text-left px-[12px] py-[10px] rounded-[10px] transition-all duration-200 ${
                              isSelected ? 'bg-[#5d76cb] text-white' : 'bg-white text-black hover:bg-[#eaeaea]'
                            }`}
                          >
                            {subcategory.name}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {selectedSubcategory && (
                <div className="bg-[#f7f7f7] rounded-[16px] p-[16px] min-h-[520px]">
                  <h3 className="text-[20px] font-bold mb-[12px] px-[12px]">Разделы</h3>
                  {selectedSubcategory.children.length === 0 ? (
                    <div className="text-gray-500">Нет разделов</div>
                  ) : (
                    <div className="flex flex-col gap-[8px] max-h-[460px] overflow-auto pr-[8px]">
                      {selectedSubcategory.children.map((leaf) => (
                        <button
                          key={leaf.id}
                          onClick={() => handleLeafSelect(leaf)}
                          className="text-left px-[12px] py-[10px] rounded-[10px] bg-white text-black hover:bg-[#eaeaea] transition-all duration-200"
                        >
                          {leaf.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="flex h-[calc(100%-72px)] flex-col lg:hidden">
              <div className="flex items-center gap-2 border-b border-[#eef1fb] px-4 py-3">
                {mobileStep > 1 && (
                  <button
                    type="button"
                    onClick={() => {
                      if (mobileStep === 3) {
                        setSelectedSubcategoryId(null);
                      } else {
                        setSelectedCategoryId(null);
                        setSelectedSubcategoryId(null);
                      }
                    }}
                    className="flex h-[36px] items-center justify-center rounded-[12px] bg-[#f3f5fc] px-3 text-[14px] font-medium text-[#4d66bb]"
                  >
                    Назад
                  </button>
                )}
                <div className="min-w-0 flex-1">
                  {selectedCategory && mobileStep > 1 && (
                    <div className="truncate text-[13px] text-[#6a7698]">
                      {mobileStep === 2 ? selectedCategory.name : `${selectedCategory.name} / ${selectedSubcategory?.name ?? ''}`}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex-1 overflow-y-auto px-3 py-3">
                <div className="space-y-2">
                  {mobileStep === 1 &&
                    categories.map((category) => (
                      <button
                        key={category.id}
                        onClick={() => {
                          if (category.children.length === 0) {
                            handleLeafSelect(category);
                            return;
                          }
                          setSelectedCategoryId(category.id);
                          setSelectedSubcategoryId(null);
                        }}
                        className="flex w-full items-center justify-between rounded-[16px] bg-[#f6f8ff] px-4 py-4 text-left text-[15px] font-medium text-black shadow-[inset_0_0_0_1px_rgba(93,118,203,0.08)] transition-colors hover:bg-[#edf2ff]"
                      >
                        <span className="pr-4">{category.name}</span>
                        <span className="text-[#5d76cb]">{category.children.length > 0 ? '›' : ''}</span>
                      </button>
                    ))}

                  {mobileStep === 2 &&
                    selectedCategory?.children.map((subcategory) => (
                      <button
                        key={subcategory.id}
                        onClick={() => {
                          if (subcategory.children.length === 0) {
                            handleLeafSelect(subcategory);
                            return;
                          }
                          setSelectedSubcategoryId(subcategory.id);
                        }}
                        className="flex w-full items-center justify-between rounded-[16px] bg-[#f6f8ff] px-4 py-4 text-left text-[15px] font-medium text-black shadow-[inset_0_0_0_1px_rgba(93,118,203,0.08)] transition-colors hover:bg-[#edf2ff]"
                      >
                        <span className="pr-4">{subcategory.name}</span>
                        <span className="text-[#5d76cb]">{subcategory.children.length > 0 ? '›' : ''}</span>
                      </button>
                    ))}

                  {mobileStep === 3 &&
                    selectedSubcategory?.children.map((leaf) => (
                      <button
                        key={leaf.id}
                        onClick={() => handleLeafSelect(leaf)}
                        className="flex w-full items-center justify-between rounded-[16px] bg-[#f6f8ff] px-4 py-4 text-left text-[15px] font-medium text-black shadow-[inset_0_0_0_1px_rgba(93,118,203,0.08)] transition-colors hover:bg-[#edf2ff]"
                      >
                        <span className="pr-4">{leaf.name}</span>
                        <span className="text-[#5d76cb]">↗</span>
                      </button>
                    ))}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
